import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { HeartHandshake, CheckCircle2 } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { company } from "../mock";

const AUDIENCES = [
  "Hospital discharge planners", "Rehab facilities", "Social workers", "Skilled nursing facilities",
  "Senior living communities", "Area Agencies on Aging", "Geriatric care managers",
  "Elder-law professionals", "Community organizations",
];

const ReferralPartners = () => {
  useSeo({
    title: "Referral Partners | Caregivers Alliance Homecare — A Home Care Resource for the Families You Serve",
    description: "Hospital discharge planners, social workers, AAAs, care managers and community organizations: add Caregivers Alliance Homecare to your referral resources. Responsive intake, clear communication, Columbus-based, statewide DODD referral coverage.",
    keywords: "home care referral partner Ohio, discharge planner home care resource Columbus, social worker home care referral Ohio",
    path: "/referral-partners",
  });

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <HeartHandshake className="h-4 w-4" /> Referral partners
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl max-w-3xl">A Home Care Resource for the Families You Serve</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            Responsive intake, clear communication and honest availability answers — so the families you serve
            aren't left waiting.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-display font-bold text-3xl text-[#0E2A33]">Who we partner with</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {AUDIENCES.map((a) => (
                <div key={a} className="flex items-center gap-3 bg-[#F7FBFC] rounded-xl border border-slate-100 p-4">
                  <CheckCircle2 className="h-5 w-5 text-[#2E9E6A] shrink-0" />
                  <span className="text-slate-700 text-sm font-medium">{a}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-8">
            <h3 className="font-display font-bold text-xl text-[#0E2A33]">What partners can expect</h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-600">
              <li>• Fast referral acknowledgement and honest staffing feasibility answers</li>
              <li>• Eligibility, authorization and participation verified before services begin</li>
              <li>• DODD Certified Provider — now accepting new patients</li>
              <li>• Private-pay and family-caregiver pathways when programs don't fit</li>
              <li>• Simple online referral form your team can use any time</li>
            </ul>
            <div className="mt-6 flex flex-col gap-3">
              <Link to="/refer">
                <Button data-testid="partners-add-btn" className="w-full rounded-full bg-[#0E5A72] hover:bg-[#0A4557] text-white h-12 font-semibold">
                  ADD CAREGIVERS ALLIANCE TO YOUR REFERRAL RESOURCES
                </Button>
              </Link>
              <Link to="/provider-sheet">
                <Button variant="outline" className="w-full rounded-full border-[#0E5A72] text-[#0E5A72] h-12 font-semibold">
                  Download our one-page provider sheet
                </Button>
              </Link>
              <a href={company.calendly} target="_blank" rel="noopener noreferrer">
                <Button data-testid="partners-book-btn" className="w-full rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white h-12 font-semibold">
                  BOOK A 15-MINUTE INTRO CALL
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ReferralPartners;
